/**
 * LoginPage - Integration in One Editorial Split
 *
 * Migrated 2026-05-16 to Integration-in-One design system.
 * Pattern: brand-ink hero (left) + login pane (right) on bg-0.
 * Tokens scoped via .iio wrapper (see frontend/src/styles/iio-tokens.css).
 */
import { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { api, setToken } from "../utils/helpers";
import { ThemeCtx } from "../theme/ThemeContext";

const IIOLogo = ({ size = 44 }) => (
  <svg
    width={size} height={size}
    viewBox="0 0 829 551"
    fill="currentColor"
    aria-label="Integration in One Logo"
    style={{ display: "block" }}
  >
    <g transform="translate(-213.299485,950.841337) scale(0.100000,-0.100000)">
      <path d="M5645 9494 c-897 -120 -1569 -787 -1719 -1705 l-6 -36 -133 -7 c-793 -42 -1457 -643 -1628 -1476 -34 -165 -35 -519 -1 -685 159 -788 794 -1407 1587 -1547 148 -26 1243 -40 2860 -35 1278 3 1197 -1 1437 67 102 28 299 106 347 137 l24 15 -52 28 c-138 76 -174 125 -486 664 l-200 345 -260 1 c-353 0 -406 -15 -581 -167 -114 -99 -136 -115 -221 -157 -136 -68 -145 -69 -606 -75 l-418 -6 -23 -57 c-142 -348 -617 -399 -824 -88 -187 280 -46 649 283 736 214 58 467 -80 546 -297 l22 -60 391 3 c371 4 394 5 456 26 89 30 158 71 232 140 120 111 192 157 309 199 71 25 84 26 349 32 211 5 275 9 274 19 -1 6 -2 39 -2 71 l-2 59 -262 5 c-402 6 -501 47 -727 302 -111 125 -170 171 -266 207 l-60 23 -801 3 -801 3 -13 -46 c-74 -264 -406 -399 -666 -271 -411 204 -313 829 143 908 213 36 450 -107 519 -314 l15 -47 822 -3 822 -3 72 -27 c152 -56 188 -81 363 -258 170 -171 209 -199 321 -231 50 -14 288 -20 456 -12 l112 6 11 31 c6 17 57 110 114 206 l103 175 -331 6 c-303 5 -337 7 -396 27 -182 60 -312 189 -377 374 -23 65 -27 94 -33 278 -10 272 -35 346 -156 459 -129 121 -176 131 -622 131 l-347 0 -33 -74 c-168 -373 -679 -386 -857 -22 -115 234 -37 494 188 630 256 153 596 17 691 -276 5 -17 30 -18 343 -18 364 0 434 -6 546 -45 106 -37 172 -80 262 -170 161 -162 205 -278 215 -575 9 -250 21 -293 109 -381 97 -97 102 -98 552 -100 l384 -2 67 114 c117 198 227 297 397 357 75 26 79 27 404 30 180 2 327 7 327 11 0 23 -136 208 -207 282 -250 259 -552 379 -956 379 l-145 0 -6 28 c-3 15 -8 47 -11 72 -71 496 -378 1021 -766 1307 -255 188 -596 333 -861 367 -26 4 -80 11 -120 16 -104 13 -375 10 -493 -6z" />
      <path d="M5070 7921 c-152 -54 -215 -237 -127 -369 122 -180 382 -136 443 74 54 186 -132 360 -316 295z" />
      <path d="M8593 6765 c-101 -28 -197 -95 -242 -169 -10 -17 -118 -202 -239 -411 -250 -429 -257 -447 -250 -610 6 -136 -8 -106 416 -835 94 -161 123 -197 199 -246 96 -62 113 -64 664 -64 723 0 655 -38 994 547 232 399 256 449 275 553 29 161 5 224 -261 680 -272 468 -300 501 -460 551 -81 25 -1006 28 -1096 4z m628 -512 c54 -46 59 -71 59 -298 l0 -205 205 0 c227 0 252 -5 298 -59 59 -70 37 -170 -45 -210 -41 -19 -60 -21 -251 -21 l-207 0 0 -199 c0 -229 -3 -251 -39 -290 -54 -58 -140 -58 -204 -1 l-32 28 -5 229 -5 228 -220 5 c-191 4 -225 7 -248 23 -88 57 -88 187 0 244 24 16 55 18 249 21 l223 3 1 221 c1 187 3 223 17 245 44 67 146 85 204 36z" />
      <path d="M4185 6539 c-197 -26 -282 -260 -149 -408 176 -196 494 -22 420 230 -35 121 -147 195 -271 178z" />
      <path d="M5052 5211 c-261 -90 -194 -476 82 -472 275 4 329 397 65 475 -66 20 -82 20 -147 -3z" />
    </g>
  </svg>
);

const SunIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);
const MoonIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const LoginPage = ({ onLogin, inviteToken }) => {
  const { t } = useTranslation("benutzer");
  const { theme, toggle } = useContext(ThemeCtx);
  const [mode, setMode] = useState("login");
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [dn, setDn] = useState("");
  const [em, setEm] = useState("");
  const [err, setErr] = useState("");
  const [ld, setLd] = useState(false);
  const [regDisabled, setRegDisabled] = useState(false);
  const [branding, setBranding] = useState({ title: "", subtitle: "" });

  // Registrierung kann serverseitig deaktiviert sein (ENV DISABLE_REGISTRATION).
  // Flag kommt aus dem oeffentlichen /api/branding. Dann nur Login anzeigen.
  // Dieselbe Antwort traegt Titel und Untertitel; beide standen hier bisher
  // fest im Markup, obwohl sie in den Einstellungen gepflegt werden.
  useEffect(() => {
    api("/api/branding")
      .then(b => {
        if (b?.registrationDisabled) { setRegDisabled(true); setMode("login"); }
        if (b) setBranding({ title: b.title || "", subtitle: b.subtitle || "" });
      })
      .catch(() => {});
  }, []);

  const go = async (e) => {
    e?.preventDefault?.();
    if (!u || !p) return;
    setErr(""); setLd(true);
    try {
      const url = mode === "login" ? "/api/auth/login" : "/api/auth/register";
      const body = mode === "login"
        ? { username: u, password: p }
        : { username: u, password: p, displayName: dn || u, email: em };
      const d = await api(url, { method: "POST", body: JSON.stringify(body) });
      setToken(d.token);
      onLogin(d.user);
    } catch (e) {
      setErr(e.message || t("login.fehlgeschlagen"));
    }
    setLd(false);
  };

  // ============================== STYLES ===================================
  const monoCaps = {
    fontFamily: "var(--iio-font-mono)",
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: ".14em",
    textTransform: "uppercase",
  };

  const wrapStyle = {
    background: "var(--iio-bg-0)",
    color: "var(--iio-text-1)",
    fontFamily: "var(--iio-font-sans)",
    fontVariantNumeric: "tabular-nums",
  };

  const heroStyle = {
    background: "var(--iio-brand-ink)",
    color: "var(--iio-hero-text)",
    padding: "var(--iio-s-12) var(--iio-s-12) var(--iio-s-8)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    position: "relative",
    overflow: "hidden",
  };

  const heroGridStyle = {
    content: "",
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(rgba(245,242,236,.04) 1px, transparent 1px),linear-gradient(90deg, rgba(245,242,236,.04) 1px, transparent 1px)",
    backgroundSize: "56px 56px",
    pointerEvents: "none",
  };

  const paneStyle = {
    background: "var(--iio-bg-0)",
    display: "flex",
    flexDirection: "column",
    padding: "var(--iio-s-12) var(--iio-s-10) var(--iio-s-6)",
    position: "relative",
  };

  const inputStyle = {
    display: "block",
    width: "100%",
    height: 40,
    padding: "0 var(--iio-s-3)",
    border: "1px solid var(--iio-border)",
    borderRadius: "var(--iio-r-sm)",
    background: "var(--iio-bg-2)",
    color: "var(--iio-text-1)",
    fontFamily: "inherit",
    fontSize: 14,
    outline: "none",
    transition: "border-color var(--iio-transition-fast), box-shadow var(--iio-transition-fast)",
  };

  // Focus ring via onFocus/onBlur for native input (no :focus pseudo without CSS file)
  const onInputFocus = (e) => {
    e.target.style.borderColor = "var(--iio-brand-ink)";
    e.target.style.boxShadow = theme === "dark"
      ? "0 0 0 3px rgba(90,154,224,.18)"
      : "0 0 0 3px rgba(10,61,74,.12)";
  };
  const onInputBlur = (e) => {
    e.target.style.borderColor = "var(--iio-border)";
    e.target.style.boxShadow = "none";
  };

  const tabBtn = (active) => ({
    appearance: "none",
    border: 0,
    background: active ? "var(--iio-brand-ink)" : "transparent",
    color: active ? "#FFFFFF" : "var(--iio-text-2)",
    fontFamily: "inherit",
    fontSize: 12,
    fontWeight: active ? 600 : 500,
    padding: "7px 14px",
    borderRadius: "var(--iio-r-xs)",
    cursor: "pointer",
    transition: "all var(--iio-transition-fast)",
  });

  const submitBtn = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "var(--iio-s-2)",
    width: "100%",
    height: 44,
    padding: "0 var(--iio-s-5)",
    border: "1px solid transparent",
    borderRadius: "var(--iio-r-md)",
    background: "var(--iio-brand-ink)",
    color: "#FFFFFF",
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 600,
    cursor: ld || !u || !p ? "not-allowed" : "pointer",
    opacity: ld || !u || !p ? 0.5 : 1,
    transition: "background var(--iio-transition-fast)",
    pointerEvents: ld ? "none" : "auto",
  };

  // ============================== RENDER ===================================
  return (
    <div className="iio iio-split" style={wrapStyle}>
      {/* Floating theme toggle */}
      <button
        type="button"
        onClick={toggle}
        aria-label={t("login.themaWechseln")}
        style={{
          position: "fixed",
          top: "var(--iio-s-4)",
          right: "var(--iio-s-4)",
          zIndex: 100,
          display: "inline-flex",
          alignItems: "center",
          gap: "var(--iio-s-2)",
          height: 32,
          padding: "0 var(--iio-s-3)",
          background: "var(--iio-bg-2)",
          color: "var(--iio-text-2)",
          border: "1px solid var(--iio-border)",
          borderRadius: "var(--iio-r-sm)",
          cursor: "pointer",
          ...monoCaps,
          fontSize: 10,
          letterSpacing: ".12em",
        }}
      >
        {theme === "dark" ? <SunIcon /> : <MoonIcon />}
        <span>{theme === "dark" ? "Light" : "Dark"}</span>
      </button>

      {/* ======================  HERO (left)  ====================== */}
      <section style={heroStyle}>
        <div style={heroGridStyle} aria-hidden="true" />

        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: "var(--iio-s-3)" }}>
          <div style={{ width: 44, height: 44, color: "var(--iio-hero-text)" }}>
            <IIOLogo size={44} />
          </div>
          <div style={{ lineHeight: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-.01em" }}>{branding.title || "Integration in One"}</div>
            {branding.subtitle && (
              <div style={{ ...monoCaps, fontSize: 9, letterSpacing: ".28em", color: "var(--iio-hero-text-3)", marginTop: 3 }}>
                {branding.subtitle}
              </div>
            )}
          </div>
        </div>

        <div style={{ position: "relative", maxWidth: 540 }}>
          <div style={{ ...monoCaps, fontSize: 11, letterSpacing: ".16em", color: "var(--iio-signal)", marginBottom: "var(--iio-s-4)" }}>
            SAP Integration Suite · 2026
          </div>
          <h1 style={{ margin: 0, fontSize: 56, lineHeight: 1.04, fontWeight: 700, letterSpacing: "-.025em" }}>
            {t("login.heroZeile1")}<br />{t("login.heroZeile2")}<br />
            <span style={{ color: "var(--iio-signal)" }}>{t("login.heroZeile3")}</span>
          </h1>
          <p style={{ marginTop: "var(--iio-s-5)", fontSize: 16, lineHeight: 1.55, color: "var(--iio-hero-text-2)", maxWidth: 460 }}>
            {t("login.heroText")}
          </p>
        </div>
      </section>

      {/* ======================  LOGIN PANE (right)  ====================== */}
      <section style={paneStyle}>
        <div style={{ ...monoCaps, fontSize: 10, color: "var(--iio-text-3)", display: "flex", gap: "var(--iio-s-4)", marginBottom: "auto" }}>
          <span>{mode === "login" ? t("login.modusAnmeldung") : t("login.modusRegistrierung")}</span>
          <span style={{ color: "var(--iio-text-4)" }}>/</span>
          <span>{t("login.authentifizierung")}</span>
        </div>

        <div style={{ width: "100%", maxWidth: 380, margin: "var(--iio-s-12) 0" }}>
          <div style={{ ...monoCaps, fontSize: 11, color: "var(--iio-text-3)", marginBottom: "var(--iio-s-2)" }}>
            {t("login.zugang")}
          </div>
          <h2 style={{ margin: "0 0 var(--iio-s-2)", fontSize: 28, fontWeight: 600, letterSpacing: "-.02em" }}>
            {mode === "login" ? t("login.willkommen") : t("login.kontoAnlegen")}
          </h2>
          <p style={{ margin: "0 0 var(--iio-s-8)", fontSize: 14, color: "var(--iio-text-2)" }}>
            {mode === "login" ? t("login.willkommenText") : t("login.kontoAnlegenText")}
          </p>

          {inviteToken && (
            <div style={{
              display: "flex", alignItems: "center", gap: "var(--iio-s-2)",
              padding: "var(--iio-s-3) var(--iio-s-4)",
              marginBottom: "var(--iio-s-5)",
              border: "1px solid var(--iio-success)",
              borderRadius: "var(--iio-r-sm)",
              color: "var(--iio-success)",
              fontSize: 13,
            }}>
              <span style={{ width: 2, height: 13, background: "currentColor", display: "inline-block" }} />
              {t("login.eingeladen")}
            </div>
          )}

          {!regDisabled && (
          <div role="tablist" style={{
            display: "inline-flex",
            border: "1px solid var(--iio-border)",
            borderRadius: "var(--iio-r-sm)",
            padding: 3,
            background: "var(--iio-bg-1)",
            marginBottom: "var(--iio-s-6)",
          }}>
            <button type="button" style={tabBtn(mode === "login")} onClick={() => { setMode("login"); setErr(""); }}>{t("login.tabAnmelden")}</button>
            <button type="button" style={tabBtn(mode === "register")} onClick={() => { setMode("register"); setErr(""); }}>{t("login.tabRegistrieren")}</button>
          </div>
          )}

          <form onSubmit={go}>
            {mode === "register" && (
              <>
                <div style={{ marginBottom: "var(--iio-s-4)" }}>
                  <label htmlFor="iio-dn" style={{ ...monoCaps, fontSize: 10, color: "var(--iio-text-3)", marginBottom: 6, display: "block" }}>{t("login.anzeigename")}</label>
                  <input id="iio-dn" style={inputStyle} value={dn} onChange={e => setDn(e.target.value)} placeholder={t("login.anzeigenamePlatzhalter")}
                    onFocus={onInputFocus} onBlur={onInputBlur} />
                </div>
                <div style={{ marginBottom: "var(--iio-s-4)" }}>
                  <label htmlFor="iio-em" style={{ ...monoCaps, fontSize: 10, color: "var(--iio-text-3)", marginBottom: 6, display: "block" }}>{t("login.email")}</label>
                  <input id="iio-em" type="email" style={inputStyle} value={em} onChange={e => setEm(e.target.value)} placeholder={t("login.emailPlatzhalter")}
                    onFocus={onInputFocus} onBlur={onInputBlur} />
                </div>
              </>
            )}
            <div style={{ marginBottom: "var(--iio-s-4)" }}>
              <label htmlFor="iio-user" style={{ ...monoCaps, fontSize: 10, color: "var(--iio-text-3)", marginBottom: 6, display: "block" }}>{t("login.benutzername")}</label>
              <input id="iio-user" autoFocus style={inputStyle} value={u} onChange={e => setU(e.target.value)} placeholder={t("login.benutzernamePlatzhalter")} autoComplete="username"
                onFocus={onInputFocus} onBlur={onInputBlur} />
            </div>
            <div style={{ marginBottom: "var(--iio-s-4)" }}>
              <label htmlFor="iio-pass" style={{ ...monoCaps, fontSize: 10, color: "var(--iio-text-3)", marginBottom: 6, display: "block" }}>{t("login.passwort")}</label>
              <input id="iio-pass" type="password" style={inputStyle} value={p} onChange={e => setP(e.target.value)} placeholder="••••••••" autoComplete={mode === "login" ? "current-password" : "new-password"}
                onFocus={onInputFocus} onBlur={onInputBlur} />
            </div>

            {err && (
              <div style={{
                display: "flex", alignItems: "center", gap: "var(--iio-s-2)",
                marginBottom: "var(--iio-s-4)",
                color: "var(--iio-critical)",
                fontSize: 13,
              }}>
                <span style={{ width: 2, height: 13, background: "currentColor", display: "inline-block" }} />
                {err}
              </div>
            )}

            <button type="submit" style={submitBtn}
              onMouseEnter={e => { if (!ld && u && p) e.currentTarget.style.background = "var(--iio-brand-ink-2)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "var(--iio-brand-ink)"; }}
              disabled={ld || !u || !p}>
              {ld ? "..." : (mode === "login" ? t("login.knopfAnmelden") : t("login.knopfKontoErstellen"))}
              {!ld && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              )}
            </button>

            <div style={{ ...monoCaps, fontSize: 10, color: "var(--iio-text-3)", marginTop: "var(--iio-s-6)" }}>
              {t("login.tls")}
            </div>
          </form>
        </div>

        <div style={{ marginTop: "auto", display: "flex", gap: "var(--iio-s-4)", ...monoCaps, fontSize: 10, color: "var(--iio-text-3)" }}>
          <span>abat AG · 2026</span>
          <span style={{ color: "var(--iio-text-4)" }}>/</span>
          <span>Build 2.0.1</span>
        </div>
      </section>
    </div>
  );
};

export default LoginPage;
